import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { getAthleteFourWeekOverview } from "../../lib/api";

function fmtWeekDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function adherenceColour(pct) {
  // Cohesive palette matching the report card email (2026-02 refresh).
  if (pct >= 0.85) return "#2F855A"; // sage — on-plan
  if (pct >= 0.6) return "#C89B3A";  // warm gold — close but light
  if (pct >= 0.3) return "#C97B5C";  // muted terracotta — patchy
  return "#7A1F2B";                   // burgundy — off-plan
}

// Friendly labels for the categorised missed-workout summary. Keys match
// the backend `missed_by_type` buckets (see athlete_portal.py).
const MISSED_TYPE_LABELS = {
  run: "run",
  strength: "strength",
  mobility: "mobility",
  cross: "cross-training",
  other: "other",
};
const MISSED_TYPE_ORDER = ["run", "strength", "mobility", "cross", "other"];

function formatMissedSummary(byType) {
  if (!byType) return "";
  const parts = [];
  for (const k of MISSED_TYPE_ORDER) {
    const n = Number(byType[k] || 0);
    if (n > 0) parts.push(`${n} ${MISSED_TYPE_LABELS[k] || k}`);
  }
  for (const [k, v] of Object.entries(byType)) {
    if (!MISSED_TYPE_LABELS[k]) {
      const n = Number(v || 0);
      if (n > 0) parts.push(`${n} ${k}`);
    }
  }
  return parts.join(" · ");
}

function fmtMinutes(min) {
  if (!min || min < 0.5) return "0";
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtKm(km) {
  if (!km || km < 0.05) return "0";
  return km >= 10 ? `${Math.round(km)}` : km.toFixed(1);
}

function MiniBars({ prescribed, actual, maxValue, unit }) {
  const max = Math.max(maxValue || 0, prescribed || 0, actual || 0, 0.01);
  const pPct = Math.min(100, (prescribed / max) * 100);
  const aPct = Math.min(100, (actual / max) * 100);
  return (
    <div
      className="flex items-end gap-0.5 h-6"
      title={`Prescribed ${prescribed} / Actual ${actual} ${unit}`}
    >
      <div className="w-2 bg-[var(--tir-border)] flex flex-col justify-end" style={{ height: "100%" }}>
        <div className="bg-[var(--tir-muted)]/40" style={{ height: `${pPct}%` }} />
      </div>
      <div className="w-2 bg-[var(--tir-border)] flex flex-col justify-end" style={{ height: "100%" }}>
        <div className="bg-[var(--tir-navy)]" style={{ height: `${aPct}%` }} />
      </div>
    </div>
  );
}

// Stacked "Prescribed: X / Actual: Y" cell — explicit labels so coaches and
// athletes never have to remember which side of the dot is which.
function PrescActual({ prescribed, actual, unit, formatter, align = "right" }) {
  const fmt = formatter || ((v) => v);
  const alignClass = align === "right" ? "text-right" : "text-left";
  return (
    <div className={`font-mono-tir text-xs whitespace-nowrap ${alignClass}`}>
      <div className="flex items-baseline gap-1.5 justify-end leading-tight">
        <span className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Presc</span>
        <span className="text-[var(--tir-muted)]">{fmt(prescribed)}{unit ? ` ${unit}` : ""}</span>
      </div>
      <div className="flex items-baseline gap-1.5 justify-end leading-tight mt-0.5">
        <span className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Actual</span>
        <span className="text-[var(--tir-ink)] font-semibold">
          {fmt(actual)}{unit ? ` ${unit}` : ""}
        </span>
      </div>
    </div>
  );
}

function AdherenceBar({ pct }) {
  const colour = adherenceColour(pct);
  const w = Math.max(0, Math.min(1, pct)) * 100;
  return (
    <div className="relative w-24 h-3 bg-[var(--tir-border)] overflow-hidden">
      <div className="absolute top-0 left-0 h-full" style={{ width: `${w}%`, background: colour }} />
    </div>
  );
}

// Tiny "Volume X%" caption that sits next to / below the adherence bar.
// Hidden when there's no prescribed metric to divide by. Coloured red-ish
// when an athlete ran significantly less than prescribed.
function VolumeBadge({ pct }) {
  if (pct == null) return null;
  const display = Math.round(pct * 100);
  let colour = "#6B6258";
  if (pct >= 0.95 && pct <= 1.15) colour = "#2F855A";
  else if (pct >= 0.75) colour = "#C89B3A";
  else if (pct < 0.75) colour = "#7A1F2B";
  return (
    <span
      className="font-mono-tir text-[10px] tracking-[0.1em] uppercase whitespace-nowrap"
      style={{ color: colour }}
      title="Volume = actual km ÷ prescribed km (or time if no distance prescribed)"
    >
      Vol {display}%
    </span>
  );
}

// Horizontal '% mileage complete' bar. Only rendered when the week (or
// totals) has prescribed distance — a strength-only block has no distance
// to complete and we hide the bar entirely rather than showing a phantom
// 0-of-0.
function MileageBar({ prescribedKm, actualKm, size = "sm" }) {
  const pre = Number(prescribedKm || 0);
  const act = Number(actualKm || 0);
  if (pre <= 0) return null;
  const raw = act / pre;
  const pct = Math.round(raw * 100);
  const fill = Math.max(1, Math.min(100, Math.round(Math.min(raw, 1) * 100)));
  let colour = "#C97B5C";
  if (raw >= 0.95) colour = "#2F855A";
  else if (raw >= 0.75) colour = "#C89B3A";
  const height = size === "lg" ? 12 : 10;
  const textSize = size === "lg" ? "text-xs" : "text-[11px]";
  return (
    <div className="mt-2" data-testid="mileage-bar">
      <div
        className="relative bg-[var(--tir-border)] overflow-hidden"
        style={{ width: "100%", maxWidth: 260, height }}
      >
        <div
          className="absolute top-0 left-0 h-full"
          style={{ width: `${fill}%`, background: colour }}
        />
      </div>
      <div className={`mt-1 font-mono-tir ${textSize} font-semibold`} style={{ color: colour }}>
        {pct}% mileage complete{" "}
        <span className="text-[var(--tir-muted)] font-normal">
          ({act.toFixed(act >= 10 ? 0 : 1)} of {pre.toFixed(pre >= 10 ? 0 : 1)} km)
        </span>
      </div>
    </div>
  );
}

function StatusPills({ completed, partial, missed }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] whitespace-nowrap">
      <span className="text-[#2F855A]" title="Workouts logged as complete">
        <strong className="font-mono-tir font-semibold">{completed}</strong>{" "}
        <span className="tracking-[0.1em] uppercase text-[10px] opacity-80">complete</span>
      </span>
      <span className="text-[#C89B3A]" title="Workouts logged as partial">
        <strong className="font-mono-tir font-semibold">{partial}</strong>{" "}
        <span className="tracking-[0.1em] uppercase text-[10px] opacity-80">partial</span>
      </span>
      <span className="text-[#7A1F2B]" title="Workouts missed or un-logged in a past week">
        <strong className="font-mono-tir font-semibold">{missed}</strong>{" "}
        <span className="tracking-[0.1em] uppercase text-[10px] opacity-80">missed</span>
      </span>
    </div>
  );
}

// Compact human-readable breakdown of the missed workouts, e.g. "Missed:
// 2 strength · 1 run". Empty when the athlete didn't miss anything.
function MissedSummary({ byType }) {
  const text = formatMissedSummary(byType);
  if (!text) return null;
  return (
    <div
      className="mt-1 font-mono-tir text-[11px] text-[#7A1F2B] leading-snug"
      data-testid="missed-summary"
    >
      <span className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-1.5">
        Missed
      </span>
      {text}
    </div>
  );
}

// "Steps: 5 / 6 days hit · 42,300 total" — only renders when the coach
// turned the daily step goal on for that week. Otherwise the row is
// hidden entirely so weeks without a step goal don't get a phantom "0/0"
// line.
function StepSummary({ summary }) {
  if (!summary || !summary.enabled) return null;
  const scheduled = Number(summary.days_scheduled || 0);
  const hit = Number(summary.days_hit || 0);
  const target = Number(summary.target || 10000);
  const total = Number(summary.actual_total || 0);
  // Colour: green when they hit every day, gold ≥50%, burgundy otherwise.
  const ratio = scheduled > 0 ? hit / scheduled : 0;
  let colour = "#7A1F2B";
  if (ratio >= 1) colour = "#2F855A";
  else if (ratio >= 0.5) colour = "#C89B3A";
  return (
    <div
      className="mt-1.5 font-mono-tir text-[11px] leading-snug flex flex-wrap items-baseline gap-x-2"
      data-testid="step-summary"
      style={{ color: colour }}
    >
      <span className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
        Steps
      </span>
      <strong className="font-semibold">
        {hit} / {scheduled} days hit
      </strong>
      <span className="text-[10px] tracking-[0.02em] text-[var(--tir-muted)]">
        · goal {target.toLocaleString()}
        {total > 0 ? ` · ${total.toLocaleString()} logged` : ""}
      </span>
    </div>
  );
}

// Adherence + km/actual sparkline across the trend window. Now split into
// two clearly labelled charts (adherence + volume) with per-chart titles,
// axis labels and a legend so the reader never has to guess what they're
// looking at.
function TrendChart({ trend }) {
  if (!trend || trend.length === 0) return null;
  const n = trend.length;
  const width = 720;
  const padL = 44;
  const padR = 12;
  const barArea = width - padL - padR;
  const barW = Math.max(4, Math.min(28, barArea / n - 2));
  const gap = n > 1 ? Math.max(1.5, (barArea - barW * n) / (n - 1)) : 0;
  const topTitleH = 26;
  const topH = 110;
  const midGap = 60;
  const botTitleH = 26;
  const botH = 110;
  const botY0 = topTitleH + topH + midGap + botTitleH;
  const maxKm = Math.max(
    ...trend.map((w) => Math.max(w.prescribed_km || 0, w.actual_km || 0)),
    1,
  );

  // ---- Adherence chart (top) ----
  const adherenceTitle = (
    <>
      <text x={padL} y={12} fontSize="11" fontWeight="700" letterSpacing="1.5" fill="#1C1917">
        WEEKLY ADHERENCE
      </text>
      <text x={padL} y={22} fontSize="9" fill="#6B6258">
        % of scheduled sessions credited (0 → 100%)
      </text>
    </>
  );
  const adherenceGrid = [0, 0.5, 1].map((frac, i) => {
    const y = topTitleH + topH - frac * (topH - 8) - 4;
    const label = `${Math.round(frac * 100)}%`;
    return (
      <g key={`a-grid-${i}`}>
        <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#E5E0D8" strokeWidth="1" />
        <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#6B6258">
          {label}
        </text>
      </g>
    );
  });
  const adherenceBars = trend.map((w, i) => {
    const pct = Math.max(0, Math.min(1, w.adherence_pct || 0));
    const h = Math.max(1, pct * (topH - 8));
    const x = padL + i * (barW + gap);
    const y = topTitleH + topH - h - 4;
    return (
      <rect
        key={`a-${i}`}
        x={x}
        y={y}
        width={barW}
        height={h}
        fill={adherenceColour(pct)}
      >
        <title>{`${w.label}: ${Math.round(pct * 100)}% adherence`}</title>
      </rect>
    );
  });

  // ---- Volume chart (bottom) ----
  const volumeTitleY = topTitleH + topH + midGap - 14;
  const volumeTitle = (
    <>
      <text x={padL} y={volumeTitleY} fontSize="11" fontWeight="700" letterSpacing="1.5" fill="#1C1917">
        WEEKLY VOLUME
      </text>
      <text x={padL} y={volumeTitleY + 10} fontSize="9" fill="#6B6258">
        Kilometres run — prescribed vs actual (peak {fmtKm(maxKm)} km)
      </text>
    </>
  );
  const kmGrid = [0, 0.5, 1].map((frac, i) => {
    const y = botY0 + botH - frac * (botH - 8) - 4;
    const v = maxKm * frac;
    return (
      <g key={`km-grid-${i}`}>
        <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#E5E0D8" strokeWidth="1" />
        <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#6B6258">
          {fmtKm(v)}
        </text>
      </g>
    );
  });
  const subW = barW / 2 - 0.5;
  const kmBars = trend.flatMap((w, i) => {
    const x = padL + i * (barW + gap);
    const pre = w.prescribed_km || 0;
    const act = w.actual_km || 0;
    const hp = Math.max(0.5, (pre / maxKm) * (botH - 8));
    const ha = Math.max(0.5, (act / maxKm) * (botH - 8));
    return [
      <rect
        key={`p-${i}`}
        x={x}
        y={botY0 + botH - hp - 4}
        width={subW}
        height={hp}
        fill="#C9C2B6"
      >
        <title>{`${w.label}: prescribed ${fmtKm(pre)} km`}</title>
      </rect>,
      <rect
        key={`act-${i}`}
        x={x + subW + 1}
        y={botY0 + botH - ha - 4}
        width={subW}
        height={ha}
        fill="#1C355E"
      >
        <title>{`${w.label}: actual ${fmtKm(act)} km`}</title>
      </rect>,
    ];
  });
  // X-axis labels shared: first, middle, last week
  const indices = Array.from(new Set([0, Math.floor(n / 2), n - 1])).sort((a, b) => a - b);
  const adherenceXLabels = indices.map((i) => {
    const x = padL + i * (barW + gap) + barW / 2;
    return (
      <text
        key={`ax-${i}`}
        x={x}
        y={topTitleH + topH + 12}
        textAnchor="middle"
        fontSize="9"
        fill="#6B6258"
      >
        {fmtWeekDate(trend[i].week_start)}
      </text>
    );
  });
  const volumeXLabels = indices.map((i) => {
    const x = padL + i * (barW + gap) + barW / 2;
    return (
      <text
        key={`vx-${i}`}
        x={x}
        y={botY0 + botH + 12}
        textAnchor="middle"
        fontSize="9"
        fill="#6B6258"
      >
        {fmtWeekDate(trend[i].week_start)}
      </text>
    );
  });

  const totalH = botY0 + botH + 24;
  return (
    <div>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${totalH}`}
        style={{ display: "block", maxWidth: width }}
        data-testid="four-week-trend-chart"
      >
        {adherenceTitle}
        {adherenceGrid}
        {adherenceBars}
        {adherenceXLabels}
        {volumeTitle}
        {kmGrid}
        {kmBars}
        {volumeXLabels}
      </svg>
      {/* Legends */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[9px] tracking-[0.14em] uppercase text-[var(--tir-muted)]">
        <span className="font-semibold text-[var(--tir-ink)] mr-1">Adherence:</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 inline-block" style={{ background: "#2F855A" }} />
          85%+
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 inline-block" style={{ background: "#C89B3A" }} />
          60–85%
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 inline-block" style={{ background: "#C97B5C" }} />
          30–60%
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 inline-block" style={{ background: "#7A1F2B" }} />
          &lt;30%
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[9px] tracking-[0.14em] uppercase text-[var(--tir-muted)]">
        <span className="font-semibold text-[var(--tir-ink)] mr-1">Volume:</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 inline-block" style={{ background: "#C9C2B6" }} />
          Prescribed km
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 inline-block" style={{ background: "#1C355E" }} />
          Actual km
        </span>
      </div>
    </div>
  );
}

export default function FourWeekOverview({ athleteId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!athleteId) return;
    setData(null);
    setError(false);
    getAthleteFourWeekOverview(athleteId)
      .then(setData)
      .catch(() => setError(true));
  }, [athleteId]);

  if (error) {
    return (
      <div className="text-xs text-[var(--tir-muted)] py-3" data-testid="four-week-overview-error">
        Couldn't load 4-week overview.
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="text-xs text-[var(--tir-muted)] py-3" data-testid="four-week-overview-loading">
        Loading 4-week overview…
      </div>
    );
  }
  const weeks = data.weeks || [];
  const trend = data.trend || [];
  const totals = data.totals || {};
  // The backend already filters pre-program empty weeks; if it gave us
  // nothing, this athlete has no scheduled work to summarise yet.
  const noData = weeks.length === 0 && trend.length === 0;

  if (noData) {
    return (
      <div
        className="border border-dashed border-[var(--tir-border)] py-6 px-4 text-xs text-[var(--tir-muted)]"
        data-testid="four-week-overview-empty"
      >
        Nothing prescribed or logged in the last 4 weeks — assign a program with a start date to populate this overview.
      </div>
    );
  }

  const maxKm = Math.max(...weeks.map((w) => Math.max(w.prescribed_km, w.actual_km)), 0.1);
  const maxMin = Math.max(...weeks.map((w) => Math.max(w.prescribed_minutes, w.actual_minutes)), 0.1);

  // Each row may know which program/week it came from; if so, the row label
  // becomes a deep-link into the planner for that week.
  const renderLabel = (w) => {
    const labelEl = (
      <>
        <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
          {w.label}
        </div>
        <div className="text-[10px] text-[var(--tir-muted)] tracking-[0.16em] uppercase">
          Week of {fmtWeekDate(w.week_start)}
        </div>
      </>
    );
    if (w.program_id && w.program_week_number) {
      return (
        <Link
          to={`/coaching/programs/${w.program_id}/edit?week=${w.program_week_number}`}
          className="block hover:text-[var(--tir-burgundy)] transition-colors"
          data-testid={`four-week-row-link-${w.weeks_ago}`}
          title={`Open planner for ${w.label}`}
        >
          {labelEl}
        </Link>
      );
    }
    return labelEl;
  };

  // Compact "card" layout for mobile (< md). The Week label is always
  // visible (vs. the desktop table which scrolls horizontally), and stats
  // are stacked top-to-bottom for natural phone reading.
  const renderMobileCard = (w, { isTotal = false } = {}) => (
    <div
      key={isTotal ? "totals" : w.week_start}
      data-testid={isTotal ? "four-week-card-totals" : `four-week-card-${w.weeks_ago}`}
      className={`px-4 py-4 border-b border-[var(--tir-border)] last:border-b-0 ${
        isTotal
          ? "bg-[var(--tir-cream)]/40 border-t-2 border-t-[var(--tir-ink)]"
          : w.weeks_ago === 0
          ? "bg-[var(--tir-cream)]/30"
          : ""
      }`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          {isTotal ? (
            <div className="text-[12px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
              {weeks.length === 4 ? "4-week totals" : `${weeks.length}-week totals`}
            </div>
          ) : (
            renderLabel(w)
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-2">
            <AdherenceBar pct={w.adherence_pct || 0} />
            <span
              className="font-mono-tir text-sm font-semibold"
              style={{ color: adherenceColour(w.adherence_pct || 0) }}
            >
              {Math.round((w.adherence_pct || 0) * 100)}%
            </span>
          </div>
          <VolumeBadge pct={w.volume_pct} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
            Distance
          </div>
          <div className="font-mono-tir text-sm whitespace-nowrap leading-snug">
            <span className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-1">Presc</span>
            <span className="text-[var(--tir-muted)]">{fmtKm(w.prescribed_km)} km</span>
            <div className="mt-0.5">
              <span className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-1">Actual</span>
              <span className="text-[var(--tir-ink)] font-semibold">{fmtKm(w.actual_km)} km</span>
            </div>
          </div>
        </div>
        <div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
            Time
          </div>
          <div className="font-mono-tir text-sm whitespace-nowrap leading-snug">
            <span className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-1">Presc</span>
            <span className="text-[var(--tir-muted)]">{fmtMinutes(w.prescribed_minutes)}</span>
            <div className="mt-0.5">
              <span className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mr-1">Actual</span>
              <span className="text-[var(--tir-ink)] font-semibold">{fmtMinutes(w.actual_minutes)}</span>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
          Workouts · of {w.scheduled || 0} scheduled
        </div>
        <StatusPills completed={w.completed || 0} partial={w.partial || 0} missed={w.missed || 0} />
        <MissedSummary byType={w.missed_by_type} />
        <MileageBar prescribedKm={w.prescribed_km} actualKm={w.actual_km} size={isTotal ? "lg" : "sm"} />
        <StepSummary summary={w.steps_summary} />
      </div>
    </div>
  );

  return (
    <div data-testid="four-week-overview" className="bg-white border border-[var(--tir-border)]">
      {/* Mobile card stack */}
      <div className="md:hidden" data-testid="four-week-overview-mobile">
        {weeks.map((w) => renderMobileCard(w))}
        {renderMobileCard(totals, { isTotal: true })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] border-b border-[var(--tir-border)]">
              <th className="text-left font-semibold px-4 py-3">Week</th>
              <th className="text-right font-semibold px-3 py-3">Distance</th>
              <th className="px-2 py-3"></th>
              <th className="text-right font-semibold px-3 py-3">Time</th>
              <th className="px-2 py-3"></th>
              <th className="text-left font-semibold px-3 py-3">Workouts</th>
              <th className="text-left font-semibold px-3 py-3">Adherence</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr
                key={w.week_start}
                data-testid={`four-week-row-${w.weeks_ago}`}
                className={`border-b border-[var(--tir-border)] last:border-b-0 ${
                  w.weeks_ago === 0 ? "bg-[var(--tir-cream)]/40" : ""
                }`}
              >
                <td className="px-4 py-3 align-middle">{renderLabel(w)}</td>
                <td className="px-3 py-3 align-middle">
                  <PrescActual prescribed={w.prescribed_km} actual={w.actual_km} unit="km" formatter={fmtKm} />
                </td>
                <td className="px-2 py-3 align-middle">
                  <MiniBars prescribed={w.prescribed_km} actual={w.actual_km} maxValue={maxKm} unit="km" />
                </td>
                <td className="px-3 py-3 align-middle">
                  <PrescActual prescribed={w.prescribed_minutes} actual={w.actual_minutes} formatter={fmtMinutes} />
                </td>
                <td className="px-2 py-3 align-middle">
                  <MiniBars prescribed={w.prescribed_minutes} actual={w.actual_minutes} maxValue={maxMin} unit="min" />
                </td>
                <td className="px-3 py-3 align-middle">
                  <StatusPills completed={w.completed} partial={w.partial} missed={w.missed} />
                  <div className="text-[10px] text-[var(--tir-muted)] tracking-[0.14em] uppercase mt-0.5">
                    of {w.scheduled} scheduled
                  </div>
                  <MissedSummary byType={w.missed_by_type} />
                </td>
                <td className="px-3 py-3 align-middle">
                  <div className="flex items-center gap-2">
                    <AdherenceBar pct={w.adherence_pct} />
                    <span
                      className="font-mono-tir text-xs font-semibold"
                      style={{ color: adherenceColour(w.adherence_pct) }}
                    >
                      {Math.round(w.adherence_pct * 100)}%
                    </span>
                  </div>
                  <div className="mt-1">
                    <VolumeBadge pct={w.volume_pct} />
                  </div>
                  <MileageBar prescribedKm={w.prescribed_km} actualKm={w.actual_km} />
                  <StepSummary summary={w.steps_summary} />
                </td>
              </tr>
            ))}
            <tr
              data-testid="four-week-row-totals"
              className="border-t-2 border-[var(--tir-ink)] bg-[var(--tir-cream)]/30"
            >
              <td className="px-4 py-3 align-middle">
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
                  {weeks.length === 4 ? "4-week totals" : `${weeks.length}-week totals`}
                </div>
              </td>
              <td className="px-3 py-3 align-middle">
                <PrescActual prescribed={totals.prescribed_km} actual={totals.actual_km} unit="km" formatter={fmtKm} />
              </td>
              <td className="px-2 py-3"></td>
              <td className="px-3 py-3 align-middle">
                <PrescActual prescribed={totals.prescribed_minutes} actual={totals.actual_minutes} formatter={fmtMinutes} />
              </td>
              <td className="px-2 py-3"></td>
              <td className="px-3 py-3 align-middle">
                <StatusPills
                  completed={totals.completed || 0}
                  partial={totals.partial || 0}
                  missed={totals.missed || 0}
                />
                <div className="text-[10px] text-[var(--tir-muted)] tracking-[0.14em] uppercase mt-0.5">
                  of {totals.scheduled || 0} scheduled
                </div>
                <MissedSummary byType={totals.missed_by_type} />
              </td>
              <td className="px-3 py-3 align-middle">
                <div className="flex items-center gap-2">
                  <AdherenceBar pct={totals.adherence_pct || 0} />
                  <span
                    className="font-mono-tir text-xs font-semibold"
                    style={{ color: adherenceColour(totals.adherence_pct || 0) }}
                  >
                    {Math.round((totals.adherence_pct || 0) * 100)}%
                  </span>
                </div>
                <div className="mt-1">
                  <VolumeBadge pct={totals.volume_pct} />
                </div>
                <MileageBar prescribedKm={totals.prescribed_km} actualKm={totals.actual_km} size="lg" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {trend.length > 0 && (
        <div className="border-t border-[var(--tir-border)] px-4 py-4 bg-[var(--tir-cream)]/20">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
              Trend · last {trend.length} week{trend.length === 1 ? "" : "s"}
            </div>
            <div className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
              {fmtWeekDate(trend[0].week_start)} → {fmtWeekDate(trend[trend.length - 1].week_start)}
            </div>
          </div>
          <TrendChart trend={trend} />
        </div>
      )}

      <FeelingTrend weeks={weeks} />

      <div className="px-4 py-3 border-t border-[var(--tir-border)] flex flex-wrap items-center gap-3">
        <div className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] flex flex-wrap gap-3 flex-1">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 bg-[var(--tir-muted)]/40 inline-block" />
            Prescribed
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 bg-[var(--tir-navy)] inline-block" />
            Actual logged
          </span>
          <span className="opacity-70">
            Adherence credits each workout; Vol % = total actual ÷ prescribed
          </span>
        </div>
        <Link
          to={`/coaching/athletes/${athleteId}/report-card`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-report-card-button"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy-dark)] text-white text-[10px] tracking-[0.22em] uppercase font-semibold transition-colors"
          title="Open the report card composer in a new tab"
        >
          <Mail size={13} /> Send Report Card
        </Link>
      </div>
    </div>
  );
}


// --- Feeling-trend dot row -------------------------------------------------
// Each scheduled day across the displayed weeks gets a dot. Filled with a
// burgundy face when the athlete logged a feeling; light grey ring when
// they didn't. Hover to see the date + sentiment.
const FEELING_PALETTE = {
  very_weak:   { color: "#7A1F2B", label: "Very weak" },
  weak:        { color: "#C97B5C", label: "Weak" },
  normal:      { color: "#9C9897", label: "Normal" },
  strong:      { color: "#2F855A", label: "Strong" },
  very_strong: { color: "#1E5C3F", label: "Very strong" },
};

function FeelingTrend({ weeks }) {
  // Build a per-week bucket of ONLY the days that were actually logged.
  // Previously we rendered 7 dots per week regardless (mostly empty
  // dashed rings) which spread the smileys out with a lot of dead
  // whitespace. Now the row clusters filled smileys tightly and shows
  // a compact "n / 7 days" caption per week for the empty context.
  const weekBuckets = [];
  let totalDays = 0;
  let totalLogged = 0;
  for (const w of weeks || []) {
    const wsIso = w.week_start;
    if (!wsIso) continue;
    let monday;
    try {
      monday = new Date(wsIso + "T00:00:00");
    } catch {
      continue;
    }
    const feelingsByDate = {};
    for (const f of (w.feelings || [])) {
      if (f?.date && f?.feeling) feelingsByDate[f.date] = f.feeling;
    }
    const items = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const feeling = feelingsByDate[iso] || null;
      if (feeling) items.push({ date: iso, feeling });
    }
    totalDays += 7;
    totalLogged += items.length;
    weekBuckets.push({
      key: wsIso,
      label: w.label || "",
      weekStart: wsIso,
      items,
    });
  }
  if (weekBuckets.length === 0) return null;
  return (
    <div
      className="border-t border-[var(--tir-border)] px-4 py-4 bg-[var(--tir-cream)]/20"
      data-testid="feeling-trend-block"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
          How they felt · {totalLogged} logged
        </div>
        <div className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
          {totalDays} day window
        </div>
      </div>
      <div className="flex flex-col gap-2" data-testid="feeling-trend-clusters">
        {weekBuckets.map((wb) => (
          <div
            key={wb.key}
            className="flex flex-wrap items-center gap-2"
            data-testid={`feeling-trend-week-${wb.weekStart}`}
          >
            <div className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] w-20 shrink-0">
              {wb.label || fmtWeekDate(wb.weekStart)}
            </div>
            {wb.items.length === 0 ? (
              <span className="text-[10px] tracking-[0.14em] uppercase text-[var(--tir-muted)] italic opacity-70">
                No feelings logged
              </span>
            ) : (
              <div className="flex flex-wrap gap-1" data-testid={`feeling-cluster-${wb.weekStart}`}>
                {wb.items.map((d) => {
                  const meta = FEELING_PALETTE[d.feeling];
                  const niceDate = (() => {
                    try {
                      return new Date(d.date + "T00:00:00").toLocaleDateString(undefined, {
                        month: "short", day: "numeric",
                      });
                    } catch {
                      return d.date;
                    }
                  })();
                  const title = `${niceDate} · ${meta?.label || d.feeling}`;
                  return (
                    <span
                      key={d.date}
                      data-testid={`feeling-dot-${d.date}`}
                      title={title}
                      aria-label={title}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: meta ? meta.color : "#9C9897" }}
                    >
                      <FeelingFace feeling={d.feeling} size={14} />
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3">
        {Object.entries(FEELING_PALETTE).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: v.color }} />
            {v.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Compact face glyph — same look as the athlete's smiley gauge but tiny.
function FeelingFace({ feeling, size = 12 }) {
  const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const mouth = {
    very_weak: "M8 17q4-4 8 0",
    weak: "M8 16q4-3 8 0",
    normal: "M8 15.5h8",
    strong: "M8 14.5q4 3 8 0",
    very_strong: "M7 14q5 5 10 0",
  }[feeling] || "M8 15.5h8";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <circle cx="9" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <path d={mouth} />
    </svg>
  );
}
